/**
 * InlineCellEditor Component
 *
 * Lightweight positioned input overlay for editing a single cell value
 * in query editor results. Handles Enter/Escape/blur. When a `smartContext`
 * is supplied, defers to `SmartCellInput` for enum/FK-aware editing — falls
 * back to the plain `<input>` otherwise.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, X } from 'lucide-react';
import { resolveCellEditVariant } from '../../common/components/cell-edit/cellEditVariant';
import { SmartCellInput } from '../../common/components/cell-edit/SmartCellInput';
import type { LooseRelationship } from '../../../../types/electron';

/**
 * Per-cell smart-editor context. Mirrors `EditableCellContext` from
 * Table Explorer so both grids share the variant decision. Optional —
 * absent context means "render the plain text input".
 */
export type InlineCellEditorContext = {
  connectionId: string;
  database: string;
  engine: string;
  schema: string;
  table: string;
  column: string;
  enumDeclared: boolean;
  enumNative: boolean;
  realFkTarget?: { schema: string; table: string; column: string };
  looseRelCandidates: readonly LooseRelationship[];
  sourceRow: Record<string, unknown> | undefined;
  dataType: string | undefined;
  /** Column nullability — drives the boolean editor's NULL option. Defaults to nullable when omitted. */
  nullable?: boolean;
};

type InlineCellEditorProps = {
  /** Visual/interaction variant */
  variant?: 'overlay' | 'side-panel';
  initialValue: unknown;
  onCommit: (newValue: unknown) => void;
  onCancel: () => void;
  smartContext?: InlineCellEditorContext;
};

function formatForEdit(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function parseEditedValue(text: string, originalValue: unknown): unknown {
  // Empty string → NULL
  if (text === '') return null;

  // Try to preserve original type
  if (typeof originalValue === 'number') {
    const num = Number(text);
    if (!isNaN(num)) return num;
  }

  if (typeof originalValue === 'boolean') {
    const lower = text.toLowerCase();
    if (lower === 'true' || lower === '1') return true;
    if (lower === 'false' || lower === '0') return false;
  }

  return text;
}

export function InlineCellEditor({
  variant = 'overlay',
  initialValue,
  onCommit,
  onCancel,
  smartContext,
}: InlineCellEditorProps) {
  const [text, setText] = useState(() => formatForEdit(initialValue));
  const inputRef = useRef<HTMLInputElement>(null);

  const smartVariant = useMemo(() => {
    if (!smartContext) return undefined;
    return resolveCellEditVariant({
      columnId: smartContext.column,
      dataType: smartContext.dataType,
      isRealForeignKey: !!smartContext.realFkTarget,
      realFkTarget: smartContext.realFkTarget,
      declaredEnumColumnIds: smartContext.enumDeclared ? new Set([smartContext.column]) : new Set(),
      nativeEnumColumnIds: smartContext.enumNative ? new Set([smartContext.column]) : new Set(),
      looseRelCandidates: smartContext.looseRelCandidates,
      sourceRow: smartContext.sourceRow,
      nullable: smartContext.nullable,
    });
  }, [smartContext]);

  if (smartContext && smartVariant && smartVariant.variant !== 'plain') {
    const enumSource = smartContext.enumDeclared ? 'declared' : 'native';
    const isSidePanel = variant === 'side-panel';
    return (
      <SmartCellInput
        variant={smartVariant}
        initialValue={initialValue}
        connectionId={smartContext.connectionId}
        database={smartContext.database}
        engine={smartContext.engine}
        schema={smartContext.schema}
        table={smartContext.table}
        column={smartContext.column}
        dataType={smartContext.dataType}
        enumSource={enumSource}
        onCommit={onCommit}
        onCancel={onCancel}
        inputClassName={
          isSidePanel
            ? 'block w-full h-7 pl-2 pr-7 text-xs bg-background border-2 border-primary/60 rounded-sm outline-none font-mono'
            : 'block w-full h-7 pl-2 pr-7 text-xs bg-background border-2 border-primary/60 rounded-sm outline-none font-mono'
        }
        // `relative w-full` (same as TE) keeps the smart editor in normal
        // flow. The plain QE input below uses `absolute inset-0` to overlay
        // the cell padding, but that positioning hijacks the popover's
        // stacking + outside-click detection — the editor mounts but the
        // popover never makes it onscreen. Sitting in normal flow matches
        // Table Explorer exactly and inherits all the picker behaviour.
        containerClassName="relative w-full"
      />
    );
  }

  useEffect(() => {
    // Use rAF to ensure focus happens after any context menu close animation
    // (Radix ContextMenu restores focus to the trigger after onClick fires)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    });
  }, []);

  const handleCommit = useCallback(() => {
    const newValue = parseEditedValue(text, initialValue);
    // Only commit if value actually changed
    const original = initialValue === undefined ? null : initialValue;
    if (newValue === original || String(newValue) === String(original)) {
      onCancel();
      return;
    }
    onCommit(newValue);
  }, [text, initialValue, onCommit, onCancel]);

  const nullable = smartContext?.nullable ?? true;
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Ctrl/Cmd+0 → commit NULL on any nullable cell. Browser intercepts
      // this for "zoom reset" — preventDefault while a cell editor is focused.
      if ((e.ctrlKey || e.metaKey) && e.key === '0') {
        if (!nullable) return;
        e.preventDefault();
        if (initialValue === null || initialValue === undefined) {
          onCancel();
          return;
        }
        onCommit(null);
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        handleCommit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    },
    [handleCommit, onCancel, nullable, initialValue, onCommit]
  );

  if (variant === 'side-panel') {
    return (
      <div className="flex items-stretch gap-1">
        <input
          ref={inputRef}
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          className="flex-1 min-w-0 h-7 px-2 text-xs bg-background border-2 border-primary/60 rounded-sm outline-none font-mono"
          data-testid="inline-cell-editor"
        />
        <button
          type="button"
          className="h-7 w-7 inline-flex items-center justify-center rounded-sm border border-border bg-muted/20 text-emerald-600 hover:bg-muted/60 hover:text-emerald-700"
          title="Confirm"
          aria-label="Confirm"
          onClick={handleCommit}
        >
          <Check className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          className="h-7 w-7 inline-flex items-center justify-center rounded-sm border border-border bg-muted/20 text-red-600 hover:bg-muted/60 hover:text-red-700"
          title="Cancel"
          aria-label="Cancel"
          onClick={onCancel}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <input
      ref={inputRef}
      type="text"
      value={text}
      onChange={(e) => setText(e.target.value)}
      onKeyDown={handleKeyDown}
      onBlur={handleCommit}
      className="absolute inset-0 z-20 px-3 py-1 text-sm bg-background border-2 border-primary/60 rounded-none outline-none font-mono"
      data-testid="inline-cell-editor"
    />
  );
}
